import React, { useEffect, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Dropdown, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";
import api from "../../../api";
import swal from "sweetalert";
import FilePreview from "../Common/FilePreview";
import PortalMenu from "../Common/PortalMenu";
import VerifiedCopyActions from "../Common/VerifiedCopyActions";

const OtherPurchaseOrderList = () => {
 const navigate = useNavigate();
 const user = useSelector((state) => state.auth?.userDetails);
 const [orders, setOrders] = useState([]);
 const [selectedPO, setSelectedPO] = useState(null);
 const [showModal, setShowModal] = useState(false);
 const [loading, setLoading] = useState(false);
 const [previewingPdf, setPreviewingPdf] = useState(false);
 const [filters, setFilters] = usePersistentFilters("other_pos_filters", {
 search: "",
 status: "",
 page: 1,
 limit: 10,
 });
 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0,
 hasPrevPage: false,
 hasNextPage: false,
 });

 const fetchOtherPurchaseOrders = async () => {
 try {
 setLoading(true);
 const queryParams = new URLSearchParams();

 if (filters.search) queryParams.append("search", filters.search);
 if (filters.status) queryParams.append("status", filters.status);
 queryParams.append("page", filters.page);
 queryParams.append("limit", filters.limit);

 const response = await api.get(
 `/purchase-orders/other?${queryParams.toString()}`,
 );

 if (response.data.success) {
 setOrders(response.data.data || []);
 if (response.data.pagination) {
 setPagination(response.data.pagination);
 }
 }
 } catch (error) {
 console.error("Error fetching other purchase orders:", error);
 swal("Error", "Failed to fetch other purchase orders", "error");
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 fetchOtherPurchaseOrders();
 }, [filters.page, filters.limit, filters.status]);

 const handleSearch = () => {
 setFilters((prev) => ({ ...prev, page: 1 }));
 fetchOtherPurchaseOrders();
 };

 const handlePageChange = (page) => {
 if (page < 1 || page > pagination.totalPages) return;
 setFilters((prev) => ({ ...prev, page }));
 };

 const statusVariant = (status) => {
 switch (status) {
 case "Issued":
 case "Accepted":
 return "primary";
 case "Delivered":
 case "Closed":
 return "success";
 case "Partially Delivered":
 return "warning";
 default:
 return "secondary";
 }
 };

 const handleView = async (id) => {
 try {
 const response = await api.get(`/purchase-orders/${id}`);
 if (response.data.success) {
 setSelectedPO(response.data.data);
 setShowModal(true);
 }
 } catch (error) {
 console.error("Error fetching other PO details:", error);
 swal(
 "Error",
 error.response?.data?.message || "Failed to load other PO details",
 "error",
 );
 }
 };

 const handlePreviewPDF = async (id) => {
 try {
 setPreviewingPdf(true);
 const res = await api.get(`/purchase-orders/${id}/pdf`, {
 responseType: "blob",
 });

 const fileURL = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" }),
 );

 window.open(fileURL, "_blank");
 } catch (error) {
 console.error("Other PO PDF preview error:", error);
 swal("Error", "Failed to preview PDF", "error");
 } finally {
 setPreviewingPdf(false);
 }
 };

 const handleDownloadPDF = async (id) => {
 try {
 const res = await api.get(`/purchase-orders/${id}/pdf`, {
 responseType: "blob",
 });

 const url = window.URL.createObjectURL(new Blob([res.data]));
 const link = document.createElement("a");
 link.href = url;
 link.download = `Other-PO-${id}.pdf`;
 link.click();
 } catch (error) {
 console.error("Other PO PDF download error:", error);
 swal("Error", "Failed to download PDF", "error");
 }
 };

 const handleDelete = async (id) => {
 const confirm = await swal({
 title: "Are you sure?",
 text: "This other purchase order will be deleted permanently",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });

 if (!confirm) return;

 try {
 const res = await api.delete(`/purchase-orders/${id}`);

 if (res.data.success) {
 swal("Deleted!", res.data.message || "Other PO deleted", "success");
 fetchOtherPurchaseOrders();
 }
 } catch (error) {
 swal(
 "Cannot Delete",
 error.response?.data?.message || "Failed to delete other PO",
 "warning",
 );
 }
 };

 const formatDate = (dateString) => {
 if (!dateString) return "-";
 try {
 return new Date(dateString).toLocaleDateString("en-IN", {
 year: "numeric",
 month: "short",
 day: "numeric",
 });
 } catch {
 return "-";
 }
 };

 const formatCurrency = (amount) => {
 if (amount === null || amount === undefined) return "Rs. 0";
 try {
 return `Rs. ${Number(amount).toLocaleString("en-IN")}`;
 } catch {
 return `Rs. ${amount}`;
 }
 };

 const editUrl = (id) => `/purchase-orders/edit/${id}?type=other`;
 const currentRole = String(user?.role?.name || user?.roleName || user?.role || "").toLowerCase();
 const canManagePO =
 currentRole === "admin" ||
 Boolean(
 (user?.permissions || []).some((permission) =>
 ["create:pos", "edit:pos", "approve:pos", "reject:pos", "delete:pos"].includes(permission)
 ) ||
 (user?.permissionNames || []).some((permission) =>
 ["create:pos", "edit:pos", "approve:pos", "reject:pos", "delete:pos"].includes(permission)
 )
 );

 const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24">
 <circle fill="#000" cx="5" cy="12" r="2"></circle>
 <circle fill="#000" cx="12" cy="12" r="2"></circle>
 <circle fill="#000" cx="19" cy="12" r="2"></circle>
 </svg>
 );

 return (
 <>
 <PageTitle activeMenu="Other Purchase Orders" motherMenu="Commercial" />

 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
 <Card.Title>Other Purchase Orders</Card.Title>
 <div className="d-flex gap-2">
 <Button variant="secondary" onClick={() => navigate("/purchase-orders-by-project")}>
 Back
 </Button>
 <Button variant="primary" onClick={() => navigate("/purchase-orders/add?type=other")}>
 Add Other PO
 </Button>
 </div>
 </Card.Header>

 <Card.Body>
 <Row className="g-2 mb-3 align-items-stretch">
 <Col md={4}>
 <Form.Control
 className="h-100"
 style={{ minHeight: "48px" }}
 placeholder="Search PO number, item, remarks"
 value={filters.search}
 onChange={(e) =>
 setFilters((prev) => ({ ...prev, search: e.target.value }))
 }
 onKeyDown={(e) => e.key === "Enter" && handleSearch()}
 />
 </Col>
 <Col md={3}>
 <Form.Select
 className="h-100"
 style={{ minHeight: "48px" }}
 value={filters.status}
 onChange={(e) =>
 setFilters((prev) => ({
 ...prev,
 status: e.target.value,
 page: 1,
 }))
 }
 >
 <option value="">All Status</option>
 <option value="Draft">Draft</option>
 <option value="Issued">Issued</option>
 <option value="Accepted">Accepted</option>
 <option value="Partially Delivered">Partially Delivered</option>
 <option value="Delivered">Delivered</option>
 <option value="Closed">Closed</option>
 </Form.Select>
 </Col>
 <Col md="auto" className="d-flex">
 <Button
 variant="outline-primary"
 className="h-100 px-4"
 style={{ minHeight: "48px" }}
 onClick={handleSearch}
 >
 Search
 </Button>
 </Col>
 </Row>

 {loading ? (
 <div className="text-center py-5">
 <Spinner />
 </div>
 ) : (
 <>
 <Table responsive bordered hover className="align-middle">
 <thead>
 <tr>
 <th style={{ textAlign: "center" }}>PO Number</th>
 <th style={{ textAlign: "center" }}>Vendor</th>
 <th style={{ textAlign: "center" }}>Items</th>
 <th style={{ textAlign: "center" }}>Status</th>
 <th style={{ textAlign: "center" }}>Total</th>
 <th style={{ textAlign: "center" }}>Created</th>
 <th className="text-center">Verified Copy</th>
 <th className="text-center">Actions</th>
 </tr>
 </thead>
 <tbody>
 {orders.length === 0 ? (
 <tr>
 <td colSpan="8" className="text-center text-muted py-4">
 No other purchase orders found
 </td>
 </tr>
 ) : (
 orders.map((po) => (
 <tr key={po._id}>
 <td style={{ textAlign: "center" }}>{po.poNumber || "-"}</td>
 <td style={{ textAlign: "center" }}>{po.vendor?.name || po.vendor?.companyName || "-"}</td>
 <td style={{ textAlign: "center" }}>{po.items?.length || 0}</td>
 <td style={{ textAlign: "center" }}>
 <Badge bg={statusVariant(po.status)}>{po.status}</Badge>
 </td>
 <td style={{ textAlign: "center" }}>Rs. {Number(po.totalAmount || 0).toLocaleString()}</td>
 <td style={{ textAlign: "center" }}>
 {po.createdAt
 ? formatDate(po.createdAt)
 : "-"}
 </td>
 <td className="text-center">
 <VerifiedCopyActions
 id={po._id}
 type="purchase-orders"
 hasVerifiedCopy={!!po.verifiedCopy?.filePath}
 verifiedCopy={po.verifiedCopy}
 onUploadSuccess={fetchOtherPurchaseOrders}
 canUpload={canManagePO}
 />
 </td>
 <td className="text-center">
 <div className="d-flex justify-content-center">
 <Dropdown>
 <Dropdown.Toggle
 variant=""
 className="btn-link i-false p-0 cursor-pointer"
 as="div"
 title="Click for more actions"
 >
 {svgMenu}
 </Dropdown.Toggle>
 <Dropdown.Menu align="end" as={PortalMenu}>
 <Dropdown.Item onClick={() => handleView(po._id)}>
 <i className="fas fa-eye me-2 text-primary"></i>
 View Details
 </Dropdown.Item>
 <Dropdown.Item
 onClick={() => handlePreviewPDF(po._id)}
 disabled={previewingPdf}
 >
 <i className="fas fa-eye me-2 text-info"></i>
 Preview PDF
 </Dropdown.Item>
 <Dropdown.Item onClick={() => handleDownloadPDF(po._id)}>
 <i className="fas fa-file-pdf me-2 text-danger"></i>
 Download PDF
 </Dropdown.Item>
 <Dropdown.Item as={Link} to={editUrl(po._id)}>
 <i className="fas fa-edit me-2 text-warning"></i>
 Edit
 </Dropdown.Item>
 <Dropdown.Divider />
 <Dropdown.Item
 className="text-danger"
 onClick={() => handleDelete(po._id)}
 >
 <i className="fas fa-trash me-2"></i>
 Delete
 </Dropdown.Item>
 </Dropdown.Menu>
 </Dropdown>
 </div>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </Table>

 <AppPagination
 currentPage={pagination.currentPage}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={orders.length}
 limit={filters.limit}
 onPageChange={handlePageChange}
 />
 </>
 )}
 </Card.Body>
 </Card>

 <Modal
 show={showModal}
 onHide={() => {
 setShowModal(false);
 setSelectedPO(null);
 }}
 size="lg"
 centered
 >
 <Modal.Header closeButton>
 <Modal.Title>PO: {selectedPO?.poNumber}</Modal.Title>
 </Modal.Header>

 <Modal.Body>
 {!selectedPO ? (
 <div className="text-center py-5">
 <div className="spinner-border text-primary"></div>
 </div>
 ) : (
 <>
 <Row className="mb-3">
 <Col md={6}>
 <p>
 <strong>PO Number:</strong>{" "}
 <span className="text-primary">{selectedPO.poNumber || "-"}</span>
 </p>
 <p>
 <strong>Vendor:</strong>{" "}
 {selectedPO.vendor?.name ||
 selectedPO.vendor?.companyName ||
 selectedPO.vendor?.email ||
 "N/A"}
 </p>
 <p>
 <strong>Status: </strong>
 <Badge bg={statusVariant(selectedPO.status)} className="rounded-pill">
 {selectedPO.status}
 </Badge>
 </p>
 </Col>

 <Col md={6}>
 <p>
 <strong>Issue Date:</strong>{" "}
 {formatDate(selectedPO.issueDate || selectedPO.createdAt)}
 </p>
 <p>
 <strong>Expected Delivery:</strong>{" "}
 {formatDate(selectedPO.expectedDeliveryDate)}
 </p>
 </Col>
 </Row>

 {(selectedPO.purchaseOrderDocument ||
 selectedPO.quotationDocument ||
 selectedPO.attachments?.length > 0) && (
 <>
 <hr />
 <h5>Documents</h5>
 <div className="d-flex flex-column gap-2">
 {selectedPO.purchaseOrderDocument && (
 <div>
 <strong>PO Document:</strong>
 <FilePreview
 file={{
 fileName: selectedPO.purchaseOrderDocument.fileName,
 fileUrl: selectedPO.purchaseOrderDocument.fileUrl,
 }}
 />
 </div>
 )}

 {selectedPO.quotationDocument && (
 <div>
 <strong>Quotation Document:</strong>
 <FilePreview
 file={{
 fileName: selectedPO.quotationDocument.fileName,
 fileUrl: selectedPO.quotationDocument.fileUrl,
 }}
 />
 </div>
 )}

 {selectedPO.attachments?.map((doc, index) => (
 <div key={index}>
 <strong>Attachment {index + 1}:</strong>
 <FilePreview
 file={{
 fileName: doc.fileName,
 fileUrl: doc.fileUrl,
 }}
 />
 </div>
 ))}
 </div>
 </>
 )}

 <hr />

 <Row>
 <Col md={6}>
 <strong>Total Amount:</strong>{" "}
 <span className="text-success fs-5">
 {formatCurrency(selectedPO.totalAmount)}
 </span>
 </Col>
 <Col md={6}>
 <strong>Tax:</strong> {formatCurrency(selectedPO.tax)}
 </Col>
 </Row>

 {selectedPO.items?.length > 0 && (
 <>
 <hr />
 <h6>Items</h6>
 <Table bordered size="sm">
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>Item</th>
 <th>Description</th>
 <th>Qty</th>
 <th>Unit</th>
 <th>Rate</th>
 <th>Amount</th>
 </tr>
 </thead>
 <tbody>
 {selectedPO.items.map((item, index) => (
 <tr key={index}>
 <td>{index + 1}</td>
 <td>
                          <strong>{item.itemName}</strong>
                          {item.subItems && item.subItems.length > 0 && (
                            <div className="mt-1 ps-2 border-start border-2 border-primary" style={{ fontSize: "0.78rem" }}>
                              <div className="text-muted fw-bold">Sub-items:</div>
                              {item.subItems.map((s, sIdx) => (
                                <div key={sIdx} className="text-secondary">
                                  • {s.name} — {s.quantity} {s.unit || item.unit || ""} {s.remarks ? `(${s.remarks})` : ""}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
 <td>{item.description || "-"}</td>
 <td>{item.quantity}</td>
 <td>{item.unit}</td>
 <td>{formatCurrency(item.rate)}</td>
 <td>{formatCurrency(item.amount)}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </>
 )}

 {selectedPO.termsAndConditions && (
 <>
 <hr />
 <h6>Terms & Conditions</h6>
 <div
 dangerouslySetInnerHTML={{ __html: selectedPO.termsAndConditions }}
 className="text-muted"
 />
 </>
 )}
 </>
 )}
 </Modal.Body>

 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowModal(false)}>
 Close
 </Button>

 {selectedPO && (
 <Button
 as={Link}
 to={editUrl(selectedPO._id)}
 variant="primary"
 onClick={() => setShowModal(false)}
 >
 <i className="fas fa-edit me-2"></i>Edit
 </Button>
 )}
 </Modal.Footer>
 </Modal>

 {previewingPdf && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{
 background: "rgba(0,0,0,0.4)",
 zIndex: 9999,
 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing Purchase Order PDF...</h6>
 </div>
 </div>
 )}
 </>
 );
};

export default OtherPurchaseOrderList;





